import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import PurchaseService from '@/services/PurchaseService';
import { PurchasesPackage } from '@revenuecat/purchases-capacitor';
import { adminAPI } from '@/api/admin';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { subscriptionsAPI } from '@/api/subscriptions';
import { Capacitor } from '@capacitor/core';
import {
  Tier,
  FREE_PROJECT_LIMIT,
  PREMIUM_PROJECT_LIMIT,
  resolveTier,
} from '@/lib/featureGates';

// ---------------------------------------------------------------------------
// Subscription model v2 — Free (lifetime, 1 slot) + Premium (5 slots)
// ---------------------------------------------------------------------------
// Single source of truth: subscription_status_view in Postgres. The view
// computes has_access server-side; free is unconditionally true, premium is
// gated by expires_at/grace_until. We do NOT recompute has_access on the
// client — that comparison was the source of the "subscription expired" race.
//
// Concurrency: refresh() is guarded by an in-flight promise mutex so parallel
// callers share one network request rather than racing each other's writes.
//
// Cache: localStorage stores the last successful status with a timestamp.
// We return stale values to the UI immediately and revalidate in the
// background within a 60s TTL window. Stale-while-revalidate, not blocking.
// ---------------------------------------------------------------------------

const STATUS_CACHE_KEY = 'santiyex-subscription-cache';
const CACHE_TTL_MS = 60 * 1000;
const REFRESH_TIMEOUT_MS = 12_000;
const INIT_TIMEOUT_MS = 15_000;

export interface Plan {
  name: string;
  price: number;
  priceString: string;
  projects: number;
  features: string[];
  identifier?: string;
  packageObject?: PurchasesPackage;
  requiresMobile: boolean;
}

interface SubscriptionViewRow {
  user_id: string;
  tier?: Tier | string | null;
  plan?: string | null;
  active_project_limit?: number | null;
  active_project_count?: number | null;
  passive_project_count?: number | null;
  expires_at?: string | null;
  grace_until?: string | null;
  projects_locked?: boolean | null;
  has_access?: boolean | null;
  subscription_status?: string | null;
  source?: string | null;
}

export interface SubscriptionStatus {
  isLoading: boolean;
  isSubscribed: boolean;
  activePlan: string | null;
  /** New canonical tier. Use this for new code. */
  tier: Tier;
  /** Back-compat alias of `tier`. Possible values: 'free' | 'premium'. */
  planType: Tier;
  projectLimit: number;
  /** Always 0 — retained for back-compat only. The trial concept is gone. */
  trialDaysRemaining: number;
  plans: Plan[];
  isExpired: boolean;
  expiresAt: Date | null;
  plan?: string | null;
  active_project_count?: number;
  active_project_limit?: number;
  projects_locked?: boolean;
  has_access?: boolean;
}

interface SubscriptionContextType {
  status: SubscriptionStatus;
  refreshSubscription: () => Promise<void>;
  refreshSubscriptionOnResume: () => Promise<void>;
  purchasePlan: (plan: Plan) => Promise<boolean>;
  restorePurchases: () => Promise<boolean>;
  redeemCode: (code: string) => Promise<boolean>;
  debugSubscriptionStatus: () => Promise<void>;
  forceRefreshFromRevenueCat: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

interface RevenueCatOffering {
  identifier: string;
  availablePackages: PurchasesPackage[];
}

const INITIAL_STATUS: SubscriptionStatus = {
  isLoading: true,
  isSubscribed: false,
  activePlan: null,
  tier: 'free',
  planType: 'free',
  projectLimit: FREE_PROJECT_LIMIT,
  trialDaysRemaining: 0,
  plans: [],
  isExpired: false,
  expiresAt: null,
  plan: null,
  active_project_count: 0,
  active_project_limit: FREE_PROJECT_LIMIT,
  projects_locked: false,
  has_access: undefined,
};

function loadCachedStatus(): SubscriptionStatus | null {
  try {
    const raw = localStorage.getItem(STATUS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { status: SubscriptionStatus; cachedAt: number };
    if (!parsed || typeof parsed.cachedAt !== 'number') return null;
    // Always return the cached value (stale-while-revalidate). The caller
    // decides whether to refresh based on age.
    return {
      ...parsed.status,
      // Cached `expiresAt` was serialised to a string; rehydrate.
      expiresAt: parsed.status.expiresAt ? new Date(parsed.status.expiresAt) : null,
      isLoading: false,
    };
  } catch {
    return null;
  }
}

function cacheAgeMs(): number {
  try {
    const raw = localStorage.getItem(STATUS_CACHE_KEY);
    if (!raw) return Number.POSITIVE_INFINITY;
    const parsed = JSON.parse(raw) as { cachedAt: number };
    return Date.now() - (parsed?.cachedAt ?? 0);
  } catch {
    return Number.POSITIVE_INFINITY;
  }
}

function persistStatus(status: SubscriptionStatus) {
  try {
    localStorage.setItem(
      STATUS_CACHE_KEY,
      JSON.stringify({ status, cachedAt: Date.now() }),
    );
  } catch {
    // ignore quota errors
  }
}

function clearStatusCache() {
  try {
    localStorage.removeItem(STATUS_CACHE_KEY);
  } catch {
    // ignore
  }
}

function statusFromViewRow(row: SubscriptionViewRow | null, plans: Plan[]): SubscriptionStatus {
  if (!row) {
    return {
      ...INITIAL_STATUS,
      isLoading: false,
      has_access: false,
      isExpired: true,
      tier: 'free',
      planType: 'free',
      plans,
    };
  }

  const tier = resolveTier(row);
  const hasAccess = row.has_access === true;
  const limit = tier === 'premium' ? PREMIUM_PROJECT_LIMIT : FREE_PROJECT_LIMIT;
  const expiresAt = row.expires_at ? new Date(row.expires_at) : null;

  return {
    isLoading: false,
    isSubscribed: tier === 'premium' && hasAccess,
    activePlan: row.plan ?? null,
    tier,
    planType: tier,
    projectLimit: limit,
    trialDaysRemaining: 0,
    plans,
    isExpired: !hasAccess,
    expiresAt,
    plan: row.plan ?? null,
    active_project_count: row.active_project_count ?? 0,
    active_project_limit: row.active_project_limit ?? limit,
    projects_locked: row.projects_locked ?? false,
    has_access: hasAccess,
  };
}

// 2-tier model: the legacy Elite SKU is still HONOURED for grandfathered
// subscribers via the webhook, but it must NEVER appear as a purchasable
// package nor be matched at purchase time. Match it loosely (identifier /
// product id / title) so it is filtered on every platform — web dummy
// offerings use `elite_monthly`, while RevenueCat may expose it as `Elite`.
function isElitePackage(pkg: PurchasesPackage): boolean {
  const haystack = [pkg.identifier, pkg.product?.identifier, pkg.product?.title]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes('elite');
}

async function fetchOfferingsAsPlans(): Promise<Plan[]> {
  try {
    const offerings = await PurchaseService.getOfferings();
    const allPackages: PurchasesPackage[] = [];

    if (offerings) {
      if ('all' in offerings && offerings.all) {
        Object.values(offerings.all as Record<string, RevenueCatOffering>).forEach((o) => {
          if (o.availablePackages?.length) allPackages.push(...o.availablePackages);
        });
      } else if ('current' in offerings && offerings.current?.availablePackages) {
        allPackages.push(...offerings.current.availablePackages);
      }
    }

    const PREMIUM_FEATURES = [
      `${PREMIUM_PROJECT_LIMIT} Aktif Proje`,
      'Tüm Para Birimleri',
      'Fotoğraf Yükleme',
      'Detaylı Rapor',
      'Öncelikli Destek',
    ];
    const PREMIUM_PRICE = 749;

    if (!allPackages.length) {
      if (PurchaseService.isMobilePlatform()) return [];
      // Web/dev fallback — single Premium offering for the new 2-tier model.
      return [
        {
          name: 'Premium',
          price: PREMIUM_PRICE,
          priceString: `${PREMIUM_PRICE} ₺`,
          projects: PREMIUM_PROJECT_LIMIT,
          features: PREMIUM_FEATURES,
          identifier: 'premium_monthly',
          requiresMobile: true,
        },
      ];
    }

    // 2-tier model: exactly ONE paid tier (Premium) may ever surface. Filter
    // out the legacy Elite SKU (see module-scope isElitePackage) and collapse
    // whatever remains to a single plan.
    const premiumPackages = allPackages.filter((pkg) => !isElitePackage(pkg));
    const chosen = (premiumPackages.length ? premiumPackages : allPackages)[0];
    if (!chosen) return [];

    const platform = Capacitor.getPlatform();
    const identifier = platform === 'android' ? chosen.identifier : 'premium_monthly';
    const price = chosen.product?.price ?? PREMIUM_PRICE;
    const priceString = chosen.product?.priceString || `${price} ₺`;
    const name = chosen.product?.title || 'Premium';

    return [
      {
        name,
        price,
        priceString,
        projects: PREMIUM_PROJECT_LIMIT,
        features: PREMIUM_FEATURES,
        identifier,
        packageObject: chosen,
        requiresMobile: false,
      },
    ];
  } catch (err) {
    console.warn('[SubscriptionContext] fetchOfferingsAsPlans failed', err);
    return [];
  }
}

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<SubscriptionStatus>(() => {
    const cached = loadCachedStatus();
    return cached ?? INITIAL_STATUS;
  });

  // Mutex: any number of refresh() callers share one in-flight promise.
  const inflightRefresh = useRef<Promise<void> | null>(null);

  // ----- Core fetch ------------------------------------------------------
  // All public functions are memoised with empty-dep useCallback so their
  // identity is STABLE across renders. Consumers can safely list them in
  // useEffect deps without causing an infinite re-render loop. Closures only
  // capture `setStatus` (stable from useState) and `inflightRefresh` (ref).

  const fetchAndApply = useCallback(async (): Promise<void> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      const next: SubscriptionStatus = {
        ...INITIAL_STATUS,
        isLoading: false,
        has_access: false,
        isExpired: true,
      };
      setStatus(next);
      clearStatusCache();
      return;
    }

    let viewRow: SubscriptionViewRow | null = null;
    try {
      viewRow = (await subscriptionsAPI.getSubscriptionStatusDetails()) as SubscriptionViewRow | null;
    } catch (err) {
      console.warn('[SubscriptionContext] view fetch failed; keeping previous status', err);
      // Don't synthesise "expired" — leave whatever cached/previous status the UI has.
      setStatus((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    const plans = await fetchOfferingsAsPlans();
    const next = statusFromViewRow(viewRow, plans);
    setStatus(next);
    persistStatus(next);
  }, []);

  // ----- Public refresh API (mutex-guarded, identity-stable) ------------

  const refreshSubscription = useCallback(async (): Promise<void> => {
    if (inflightRefresh.current) {
      return inflightRefresh.current;
    }

    const work = (async () => {
      setStatus((prev) => ({ ...prev, isLoading: true }));

      const timeoutId = setTimeout(() => {
        setStatus((prev) => ({ ...prev, isLoading: false }));
      }, REFRESH_TIMEOUT_MS);

      try {
        if (Capacitor.isNativePlatform()) {
          try {
            await PurchaseService.ensureRcLogin();
          } catch (err) {
            console.warn('[SubscriptionContext] RC login skipped', err);
          }
          PurchaseService.clearCustomerInfoCache();
        }

        const refreshes: Promise<unknown>[] = [fetchAndApply()];
        if (Capacitor.isNativePlatform()) {
          refreshes.push(PurchaseService.refreshSubscription().catch(() => null));
        }
        await Promise.all(refreshes);
      } finally {
        clearTimeout(timeoutId);
      }
    })().finally(() => {
      inflightRefresh.current = null;
    });

    inflightRefresh.current = work;
    return work;
  }, [fetchAndApply]);

  const refreshSubscriptionOnResume = useCallback(async (): Promise<void> => {
    if (Capacitor.isNativePlatform()) {
      try {
        await PurchaseService.ensureRcLogin();
        const customerInfo = await PurchaseService.restorePurchases();
        const hasActive = Object.keys(customerInfo.entitlements.active).length > 0;
        if (hasActive) {
          await PurchaseService.refreshSubscription();
        }
      } catch (err) {
        console.warn('[SubscriptionContext] resume restore failed', err);
      }
    }
    await refreshSubscription();
  }, [refreshSubscription]);

  // ----- Lifecycle -------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    let initTimeout: ReturnType<typeof setTimeout> | null = null;

    const init = async () => {
      // Hard ceiling so the UI never sits in "loading" forever.
      initTimeout = setTimeout(() => {
        if (cancelled) return;
        setStatus((prev) => ({ ...prev, isLoading: false }));
      }, INIT_TIMEOUT_MS);

      try {
        await refreshSubscription();
      } finally {
        if (initTimeout) clearTimeout(initTimeout);
      }
    };

    init();

    // Stale-while-revalidate: if cache exists but is older than the TTL,
    // kick off an async refresh after init settles.
    if (cacheAgeMs() > CACHE_TTL_MS) {
      void refreshSubscription();
    }

    return () => {
      cancelled = true;
      if (initTimeout) clearTimeout(initTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auth state listener
  useEffect(() => {
    let mounted = true;

    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === 'SIGNED_IN' && session?.user) {
        clearStatusCache();
        setStatus((prev) => ({ ...prev, isLoading: true, has_access: undefined }));
        // Give Supabase a tick to finalise the session before we query.
        setTimeout(() => {
          if (mounted) void refreshSubscription();
        }, 500);
      } else if (event === 'SIGNED_OUT') {
        clearStatusCache();
        setStatus({
          ...INITIAL_STATUS,
          isLoading: false,
          has_access: false,
          isExpired: true,
        });
      }
    });

    const handleForceRefresh = () => {
      if (!mounted) return;
      localStorage.removeItem('santiyex-force-subscription-refresh');
      void refreshSubscription();
    };
    window.addEventListener('santiyex-force-subscription-refresh', handleForceRefresh);

    return () => {
      mounted = false;
      authSub?.unsubscribe();
      window.removeEventListener('santiyex-force-subscription-refresh', handleForceRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- Purchases / restore / redeem (also memoised for stable identity) -

  const purchasePlan = useCallback(async (plan: Plan): Promise<boolean> => {
    try {
      setStatus((prev) => ({ ...prev, isLoading: true }));

      const offerings = await PurchaseService.getOfferings();
      let matched: PurchasesPackage | undefined;

      // The exact product the user was shown. fetchOfferingsAsPlans already
      // filtered out the legacy Elite SKU and resolved the displayed plan to
      // the Premium package, so we re-match on THAT product id to purchase
      // precisely what was displayed. This is what prevents the 999₺ Elite SKU
      // from ever being charged when the user taps the 749₺ Premium plan.
      const targetProductId = plan.packageObject?.product?.identifier;

      if (offerings && 'all' in offerings && offerings.all) {
        for (const offering of Object.values(offerings.all || {}) as RevenueCatOffering[]) {
          matched = offering.availablePackages.find((pkg) => {
            // Never purchase the legacy Elite SKU under any circumstances.
            if (isElitePackage(pkg)) return false;
            // Preferred: the precise product the user saw on the plan card.
            if (targetProductId) return pkg.product?.identifier === targetProductId;
            // Fallbacks (no packageObject captured): match by identifier, or
            // accept any remaining non-Elite premium package.
            if (pkg.identifier === plan.identifier) return true;
            return plan.identifier === 'premium_monthly';
          });
          if (matched) break;
        }
      }

      // Last resort: the exact package object we displayed (never Elite).
      if (!matched && plan.packageObject && !isElitePackage(plan.packageObject)) {
        matched = plan.packageObject;
      }

      if (!matched) {
        toast({
          title: 'Abonelik bulunamadı',
          description: 'Lütfen tekrar deneyin.',
          variant: 'destructive',
        });
        setStatus((prev) => ({ ...prev, isLoading: false }));
        return false;
      }

      const result = await PurchaseService.purchasePackage(matched);
      const hasActive = Object.keys(result.customerInfo.entitlements.active).length > 0;

      if (hasActive) {
        await PurchaseService.refreshOfferings();
        await PurchaseService.refreshSubscription();
        await refreshSubscription();
        return true;
      }
      setStatus((prev) => ({ ...prev, isLoading: false }));
      return false;
    } catch (err) {
      console.error('[SubscriptionContext] purchasePlan failed', err);
      toast({
        title: 'Hata',
        description: 'Abonelik satın alınırken bir sorun oluştu.',
        variant: 'destructive',
      });
      setStatus((prev) => ({ ...prev, isLoading: false }));
      return false;
    }
  }, [refreshSubscription]);

  const restorePurchases = useCallback(async (): Promise<boolean> => {
    try {
      setStatus((prev) => ({ ...prev, isLoading: true }));
      const customerInfo = await PurchaseService.restorePurchases();
      const hasActive = Object.keys(customerInfo.entitlements.active).length > 0;
      await refreshSubscription();
      return hasActive;
    } catch {
      setStatus((prev) => ({ ...prev, isLoading: false }));
      return false;
    }
  }, [refreshSubscription]);

  const redeemCode = useCallback(async (code: string): Promise<boolean> => {
    try {
      setStatus((prev) => ({ ...prev, isLoading: true }));
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({ title: 'Hata', description: 'Kullanıcı bilgisi alınamadı', variant: 'destructive' });
        setStatus((prev) => ({ ...prev, isLoading: false }));
        return false;
      }
      const result = await adminAPI.redeemCode(user.id, code);
      if (result.success) {
        await refreshSubscription();
        return true;
      }
      toast({
        title: 'Kod Geçersiz',
        description: result.message || 'Girdiğiniz kod geçersiz veya kullanılmış.',
        variant: 'destructive',
      });
      setStatus((prev) => ({ ...prev, isLoading: false }));
      return false;
    } catch {
      toast({
        title: 'Hata',
        description: 'Kod kullanılırken bir hata oluştu.',
        variant: 'destructive',
      });
      setStatus((prev) => ({ ...prev, isLoading: false }));
      return false;
    }
  }, [refreshSubscription]);

  // ----- Debug helpers --------------------------------------------------

  const debugSubscriptionStatus = useCallback(async (): Promise<void> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const customerInfo = await PurchaseService.getCustomerInfo();
      const viewRow = await subscriptionsAPI.getSubscriptionStatusDetails();
      const table = await subscriptionsAPI.getSubscriptionFromTable();
      console.log('[SubscriptionContext debug]', { customerInfo, viewRow, table });
    } catch (err) {
      console.error('[SubscriptionContext debug] failed', err);
    }
  }, []);

  const forceRefreshFromRevenueCat = useCallback(async (): Promise<void> => {
    try {
      await PurchaseService.refreshSubscription();
      clearStatusCache();
      await refreshSubscription();
    } catch (err) {
      console.error('[SubscriptionContext] forceRefreshFromRevenueCat failed', err);
      throw err;
    }
  }, [refreshSubscription]);

  return (
    <SubscriptionContext.Provider
      value={{
        status,
        refreshSubscription,
        refreshSubscriptionOnResume,
        purchasePlan,
        restorePurchases,
        redeemCode,
        debugSubscriptionStatus,
        forceRefreshFromRevenueCat,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default useSubscription;
